import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { BehavioralProfileTypes, GetDepartmentsRecords, PhasrBehavioralProfilesComponentSchema } from '../types';
import { PhasrRestrictedProfilesViewWebComponent } from '../../phasrRestrictedProfilesSelector/phasrRestrictedProfilesView';
import { BehaviorProfileListItem } from '../../phasrRestrictedProfilesSelector/types';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { translateFn } from '../../../utils/translator.helper';
import { GzSingleSelectComplexWebComponent } from '../../../ui/gzSingleSelectComplex';
import { API_TYPE_PHASR_BEHAVIORAL_IDENTITIES } from '../../../webcomponents.constants';
import { GetItemsResponse } from '../../../ui/gzSingleSelectComplex/types';
import { GzSingleSelectWebComponent } from '../../../ui/gzSingleSelectDropdown';
import { Collections } from '../../../phasrApplyActionPanel/types';
import { isEmpty } from 'lodash';
import { GzInfoBoxWebComponent } from '../../../ui/gzInfoBox';
import { toggleVisibility } from '../../../utils/htmlElement.utils';

export class PhasrBehavioralProfilesComponentView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $behavioralProfilesTitle: GzTitleWebComponent;
    $infoBox: GzInfoBoxWebComponent;
    $behavioralProfiles: PhasrRestrictedProfilesViewWebComponent;
    $profileTypeSelector: GzSingleSelectWebComponent;
    $searchUsers: GzSingleSelectComplexWebComponent;
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$behavioralProfiles = this.shadow.querySelector('gz-phasr-restricted-profiles-view');
    this.dom.$infoBox = this.shadow.querySelector('#infoBox');
    this.dom.$behavioralProfilesTitle = this.shadow.querySelector('#behavioralProfilesTitle');
    this.dom.$searchUsers = this.shadow.querySelector('#phasrSearchUsers');
    this.dom.$profileTypeSelector = this.shadow.querySelector('#phasrProfileTypeSelector');

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$behavioralProfilesTitle.innerText = translateFn('Behavioral profiles');
    this.dom.$profileTypeSelector.placeholder = translateFn('Select behavioral profile type');
    this.dom.$profileTypeSelector.label = translateFn('Select behavioral profile type');
    this.dom.$searchUsers.placeholder = this.dependencies.translateFn('Select user');
    this.dom.$searchUsers.searchPlaceholderString = this.dependencies.translateFn('Search');
    this.dom.$searchUsers.noSearchResultsString = this.dependencies.translateFn('No results found.');
  }

  public setUsersDropdown(data: PhasrBehavioralProfilesComponentSchema): void {
    this.dom.$searchUsers.resetSelectorDropdown();
    this.dom.$searchUsers.readonly = false;
    this.dom.$searchUsers.disabled = false;

    this.dom.$searchUsers.apiType = API_TYPE_PHASR_BEHAVIORAL_IDENTITIES;

    const companyData: GetItemsResponse = {
      items: [],
      totalItemsCount: 0,
      context: {
        objectId: data.id,
        collection: data.collection,
        companyId: data.companyId,
        mode: data.behavioralProfileType,
      },
    };

    this.dom.$searchUsers.setFilterOptions(companyData).then().catch();
  }

  private populateProfileTypeDropdown(data: PhasrBehavioralProfilesComponentSchema): void {
    this.dom.$profileTypeSelector.classList.remove('hidden');
    this.dom.$profileTypeSelector.filterOptions = [
      {
        label: translateFn('Direct control'),
        value: BehavioralProfileTypes.DIRECT_CONTROL,
      },
      {
        label: translateFn('Autopilot'),
        value: BehavioralProfileTypes.AUTOPILOT,
      },
    ];
    this.dom.$profileTypeSelector.value = data.behavioralProfileType;
    this.dom.$profileTypeSelector.cannotDelete = true;
  }

  buildSuccessView(data: PhasrBehavioralProfilesComponentSchema) {
    this.dom.$wrapper.classList.add('is-loading');
    this.setUsersDropdown(data);
    this.setBehavioralProfiles(data);
    if (data.collection === Collections.RULES) {
      this.populateProfileTypeDropdown(data);
    }
    this.dom.$wrapper.classList.remove('is-loading');
  }

  public setInfoBox(behavioralProfileType: BehavioralProfileTypes): void {
    this.dom.$infoBox.innerHTML =
      `<span>${
        behavioralProfileType === BehavioralProfileTypes.DIRECT_CONTROL
        ? translateFn("Select profiles to restrict access, or deselect them to allow it.")
        : translateFn("Behavioral profiles are automatically managed by Autopilot. Manual control over access permissions is not available.")
      }</span>`;
    toggleVisibility(this.dom.$infoBox, true);
  }

  public setBehavioralProfiles(data: PhasrBehavioralProfilesComponentSchema): void {
    // First, we need to get the departments and the total profiles by department, then load them in the view.
    // Additionally, we need to store the total profiles by department when the component is initialized, so we can correctly update the checkboxes states for users and profiles, in case a department is selected without loading its users from the backend.

    this.dom.$behavioralProfiles.departments = this.getDepartmentsForUi(data.departments ?? []);

    if (isEmpty(this.dom.$behavioralProfiles.departments)) {
      this.dom.$behavioralProfiles.departmentsList.emptyStateMessage = translateFn('No data');
    } else if (this.dom.$behavioralProfiles.departments.length === 1 && this.dom.$behavioralProfiles.departments[0].name === 'Unassigned') {
      this.dom.$behavioralProfiles.dispatchEvent(new CustomEvent('departments-select', {detail: this.dom.$behavioralProfiles.departments[0]}));
    }

    this.dom.$behavioralProfiles.selectedProfilesCount = this.getCountsInAllItems(
      data.departments ?? []
    ).totalRestrictedItems;
    this.dom.$behavioralProfiles.requestUpdate();
  }

  public getDepartmentsForUi(departments: GetDepartmentsRecords[]): Array<BehaviorProfileListItem> {
    return departments.map(department => {
      return {
        id: department.behavioralProfileDepartment,
        name: department.behavioralProfileDepartmentName,
        status: department.status,
        totalProfiles: department.totalProfiles,
        totalRestrictedProfiles: department.restrictedProfiles,
      };
    });
  }

  public getCountsInAllItems(departments: GetDepartmentsRecords[]): {
    totalItems: number;
    totalRestrictedItems: number;
  } {
    const totalRestrictedItems = departments.reduce((acc, department) => acc + department.restrictedProfiles, 0);
    const totalItems = departments.reduce((acc, department) => acc + department.totalProfiles, 0);

    return {
      totalItems,
      totalRestrictedItems,
    };
  }
}
